import './CasesEditPage.css'
import { useEffect, useRef, useState } from 'react'
import { AlertMessage, IDateSearchLocalStorageNames, IParamsType, IRootState } from 'types'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormClassComponent, FormElement, FormHandle } from '@progress/kendo-react-form'
import { Badge } from 'react-bootstrap'
import { LS_SIDEBAR_MENU_STATE } from 'core/consts'
import { isMobile } from 'react-device-detect'
import { V1ApiCasesClient, V1ApiDealersClient, V1ApiSharedClient } from 'core/http-common'
import { focusFirstErrorByForm, getErrors } from 'core/helpers'
import { useDispatch } from 'react-redux'
import { DealerDto, DealerStatusDto, ProductTypeEnum } from '@v1DealersApi'
import CaseInformationForm from 'components/PageForms/Cases/CaseInformationForm'
import HistoryForm from 'components/Shared/HistoryGrid'
import NotesForm from 'components/PageForms/Cases/NotesForm'
import AttachmentsForm from 'components/PageForms/Cases/AttachmentsForm'
import ContactHistoryForm from 'components/PageForms/Cases/ContactHistoryForm'
import MessageAlert from 'components/Alerts/MessageAlert/MessageAlert'
import StickyHeaderWrapper from 'components/StickyHeader/StickyHeaderWrapper'
import DefaultButton from 'components/Buttons/DefaultButton/DefaultButton'
import SaveButton from 'components/Buttons/SaveButton/SaveButton'
import SideNavWrapper from 'components/SideNav/SideNavWrapper'
import SideNav from 'components/SideNav/SideNav'
import SideNavMenu from 'components/SideNav/SideNavMenu'
import SideNavMenuItem from 'components/SideNav/SideNavMenuItem'
import SideNavContent from 'components/SideNav/SideNavContent'
import ProductsForm from 'components/Dealers/ProductsForm'
import { HistoryGrid, Watcher } from 'components'
import { BulkIdsDto, CaseDto } from 'generated/v1/v1CasesApi'
import { AppDispatch } from 'state/store'
import { endLoadingAction, startLoadingAction } from 'state/actions/appActions'
import { GetDataResponse, GridGetDataProps, ISAGridRef } from 'components/Grid/SAGrid'
import toast from 'core/toast'
import { NoteTypeEnum, AttachmentTypeEnum } from 'generated/v1/v1SharedApi'
import { WatcherTypeEnum } from 'components/Watcher/Watcher'
import { IFormDateSpanSelectorRef } from 'components/Form/FormDateSpanSelector'
import moment from 'moment'
import { faGlobe } from '@fortawesome/free-solid-svg-icons'

export enum EditCasesSectionTypeEnum {
    CASE_SUMMARY = 'case-summary',
    PRODUCTS = 'products',
    HISTORY = 'history',
    NOTES = 'notes',
    ATTACHMENTS = 'attachments',
    CONTACT_HISTORY = 'contact-history',
}

enum DealerStatusEnum {
    Undefined = 'Undefined',
    Active = 'Active',
    PastDue = 'PastDue',
    Parked = 'Parked',
    Saved = 'Saved',
    Cancelled = 'Cancelled',
    Deleted = 'Deleted',
    OutsideCollections = 'OutsideCollections',
    DeletedForNonPayment = 'DeletedForNonPayment',
    AtRisk = 'AtRisk',
}

export interface IParams extends IParamsType {
    id: string
    dealerId: string
    section: string
}

const CasesEditPage = () => {
    const { id, section } = useParams<IParams>()
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()

    const widthRef = useRef(window.innerWidth)
    const baseUrl = `/cases/edit/${id}`
    const loading = false ///useSelector((state: IRootState) => state.app.loading)
    const [dealer, setDealer] = useState<DealerDto>()
    const [dealerStatus, setDealerStatus] = useState<DealerStatusDto>()

    const [caseToEdit, setCaseToEdit] = useState<CaseDto>()

    const [showSideNavMenu, setShowSideNavMenu] = useState<boolean>(true)
    const [selectedSection, setSelectedSection] = useState<EditCasesSectionTypeEnum>(
        section ? section as EditCasesSectionTypeEnum : EditCasesSectionTypeEnum.CASE_SUMMARY
    )
    const [alert, setAlert] = useState<AlertMessage>()
    const [formIsModified, setFormIsModified] = useState<boolean>(false)

    const dateSpanRef = useRef<IFormDateSpanSelectorRef>(null)
    const caseHistoryGridRef = useRef<ISAGridRef>(null)
    const formDateSpanSelectorRef = useRef<FormHandle>(null)
    const caseInformationRef = useRef<FormClassComponent>(null)

    const maxDate = moment().endOf('day').toDate()

    const searchByDate = async (start: Date, end: Date) => {
        formDateSpanSelectorRef.current?.valueSetter('startDate', start)
        formDateSpanSelectorRef.current?.valueSetter('endDate', end)
        caseHistoryGridRef.current?.resetAndFetch()
    }

    const sectionsToHideSaveButton = [
        EditCasesSectionTypeEnum.ATTACHMENTS,
        EditCasesSectionTypeEnum.HISTORY,
        EditCasesSectionTypeEnum.NOTES,
        EditCasesSectionTypeEnum.CONTACT_HISTORY,
        EditCasesSectionTypeEnum.PRODUCTS,
    ]

    //when the form is modified this is the dto that will be saved when the user clicks save in the save or discard modal
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [dtoToSave, setDtoToSave] = useState<any>()
    const [selectedSectionForm, setSelectedSectionForm] = useState<FormClassComponent>()

    useEffect(() => {
        const storedSidebarStatus = localStorage.getItem(LS_SIDEBAR_MENU_STATE)
        if (storedSidebarStatus)
            setShowSideNavMenu(storedSidebarStatus === 'true')
    }, [])

    ///close sidebar on specific widths
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth === widthRef.current)
                return

            if (window.innerWidth < 1325) {
                setShowSideNavMenu(false)
                localStorage.setItem(LS_SIDEBAR_MENU_STATE, 'false')

            } else {
                setShowSideNavMenu(true)
                localStorage.setItem(LS_SIDEBAR_MENU_STATE, 'true')
            }

            widthRef.current = window.innerWidth
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const fetchDealer = async () => {
        if (!caseToEdit?.dealerId) {
            // navigate(redirectUrl)
            return
        }

        try {
            //await dispatch(startLoadingAction())
            setAlert({ variant: 'info', messages: [] })

            const [
                dealer,
                dealerStatus,
            ] = await Promise.all([
                V1ApiDealersClient.getDealer(caseToEdit?.dealerId),
                V1ApiDealersClient.getDealerStatus(caseToEdit?.dealerId)
            ])

            setDealer(dealer)
            setDealerStatus(dealerStatus)
        }
        catch (e) {
            setAlert({ variant: 'danger', messages: getErrors(e) })
        }
        finally {
            window.scrollTo(0, 0)
        }
    }

    const fetchCase = async () => {
        if (!id || !id.trim() || isNaN(Number(id))) {
            // navigate(redirectUrl)
            return
        }

        try {
            await dispatch(startLoadingAction())
            setAlert({ variant: 'info', messages: [] })
            let caseToEdit = await V1ApiCasesClient.getCase(parseInt(id, 10))

            if (caseToEdit) {
                setCaseToEdit(caseToEdit)
                // Check for Watchers
                const watchers = await V1ApiSharedClient.caseWatchers(caseToEdit.caseId)
                if (watchers.length > 0) {
                    await V1ApiCasesClient.notifyCaseWatchers(caseToEdit.caseId, new BulkIdsDto({ ids: watchers }))
                }
            } else {
                // Handle the case where caseToEdit is undefined
                console.warn('No case found')
                // You might want to navigate to an error page or show an alert here
            }
        }
        catch (e) {
            setAlert({ variant: 'danger', messages: getErrors(e) })
        } finally {
            await dispatch(endLoadingAction())
        }
    }

    const handleRefreshCase = async () => {
        await fetchCase()
    }

    const fetchCaseHistoryData = async (props: GridGetDataProps): Promise<GetDataResponse> => {
        try {
            let values = formDateSpanSelectorRef.current?.values
            if (caseToEdit?.caseId != null) {
                const result = await V1ApiCasesClient.getCaseHistory(
                    caseToEdit.caseId,
                    props.search,
                    props.sortBy,
                    props.sortAscending,
                    props.paging && props.paging.currentPage ? props.paging.currentPage : 1,
                    props.paging ? props.paging.take : 25,
                    true,
                    values?.startDate ? values.startDate : undefined,
                    values?.endDate ? values.endDate : undefined,

                )
                return {
                    data: result.data,
                    total: result.totalItems ? result.totalItems : 0
                }
            }

            else {
                return {
                    data: [],
                    total: 0
                }
            }
        }
        catch (e) {
            toast.responseErrors(e)
            return {
                data: [],
                total: 0
            }
        }
    }

    useEffect(() => {
        fetchCase()

    }, [])

    useEffect(() => {
        fetchDealer()
    }, [caseToEdit])


    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFormChange = (modified: boolean, form: FormClassComponent, dto: any) => {
        setFormIsModified(modified)
        setSelectedSectionForm(form)
        setDtoToSave(dto)
    }

    const changeSection = (sectionType: EditCasesSectionTypeEnum) => {
        if (isMobile) {
            setShowSideNavMenu(false)
        }

        if (selectedSection === sectionType)
            return

        //if form is not modified just change section and update url
        if (!formIsModified) {
            setSelectedSection(sectionType)
            //special redirect that doesn't cause page render with side nav
            window.history.replaceState(null, '', `${baseUrl}/${sectionType}`)
            window.scrollTo(0, 0)
            return
        }
        window.scrollTo(0, 0)
    }

    const hasEditInventoryPermission = true// hasPermission(DcsPermissions.EditInventory)

    const editable = hasEditInventoryPermission

    const SectionHandler = (selectedStep: EditCasesSectionTypeEnum) => {
        switch (selectedStep) {
            case EditCasesSectionTypeEnum.CASE_SUMMARY:
                return <CaseInformationForm
                    productType={ProductTypeEnum.Case}
                    caseToEdit={caseToEdit ?? {} as CaseDto}
                    editable={editable}
                    caseFormRef={caseInformationRef}
                    onCaseUpdated={handleRefreshCase}
                />
            case EditCasesSectionTypeEnum.PRODUCTS:
                return <ProductsForm
                    dealerId={caseToEdit?.dealerId}
                    productType={ProductTypeEnum.Case}
                    formIsModified={formIsModified}
                    editable={editable}
                />
            case EditCasesSectionTypeEnum.HISTORY:
                return <HistoryGrid
                    type={'Case'}
                    identifierPropertyName='caseHistoryId'
                    getHistoryData={fetchCaseHistoryData}
                    historyGridRef={caseHistoryGridRef}
                    searchByDate={searchByDate}
                    maxDate={maxDate}
                    dateSpanRef={dateSpanRef}
                    historyDateSpanFormRef={formDateSpanSelectorRef}
                />
            case EditCasesSectionTypeEnum.NOTES:
                return <NotesForm
                    noteTypeId={caseToEdit?.caseId}
                    noteType={NoteTypeEnum.Case}
                    formIsModified={formIsModified}
                    editable={editable}
                    hideDateOptions={true}
                />
            case EditCasesSectionTypeEnum.ATTACHMENTS:
                return <AttachmentsForm
                    attachmentTypeId={caseToEdit?.caseId}
                    attachmentType={AttachmentTypeEnum.Case}
                    formIsModified={formIsModified}
                    editable={editable}
                />
            case EditCasesSectionTypeEnum.CONTACT_HISTORY:
                return <ContactHistoryForm
                    caseToEdit={caseToEdit}
                    editable={editable}
                />
            default:
                return <CaseInformationForm
                    productType={ProductTypeEnum.Case}
                    caseToEdit={caseToEdit ?? {} as CaseDto}
                    editable={editable}
                    caseFormRef={caseInformationRef}
                    onCaseUpdated={handleRefreshCase}
                />
        }
    }

    const sectionActive = (array: Array<EditCasesSectionTypeEnum>) => {
        return array.some(a => a === selectedSection)
    }

    const itemActive = (item: EditCasesSectionTypeEnum) => item === selectedSection

    const statusColorMap: Record<number, string> = {
        1: 'bg-success', // Active
        2: 'bg-warning', // PastDue
        3: 'bg-warning', // Parked
        4: 'bg-danger',  // Cancellation Request
        5: 'bg-success', // Saved
        6: 'bg-danger',  // Cancelled
        7: 'bg-danger',  // Deleted
        1001: 'bg-danger', // Deleted
        2001: 'bg-danger', // Outside Collections
        2002: 'bg-danger', // Deleted For Non-Payment
        2003: 'bg-orange', // At Risk
        0: 'bg-secondary' // Unknown
    }

    const badgeColor = statusColorMap[dealerStatus?.dealerStatusId ?? 0] || 'bg-secondary'
    // if (!hasPermission(DcsPermissions.EditInventory))
    //     return (
    //         <MessageAlert
    //             alert={{
    //                 variant: 'danger',
    //                 messages: [DEFAULT_PERMISSION_MESSAGE]
    //             }}
    //         />
    //     )

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const submitClick = async (event: any) => {
        if (!caseInformationRef.current)
            return

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await caseInformationRef.current?.onSubmit(caseInformationRef.current?.values as any)

        if (!caseInformationRef.current.isValid()) {
            await focusFirstErrorByForm(caseInformationRef.current)
            return
        }
    }

    const getDeletedState = () => {
        let dealerStatusEnum = DealerStatusEnum.Active
        if (dealerStatus?.dealerStatus && Object.values(DealerStatusEnum).includes(dealerStatus.dealerStatus as DealerStatusEnum)) {
            dealerStatusEnum = dealerStatus.dealerStatus as DealerStatusEnum
        }

        return dealerStatusEnum === DealerStatusEnum.Cancelled ||
            dealerStatusEnum === DealerStatusEnum.Deleted ||
            dealerStatusEnum === DealerStatusEnum.OutsideCollections ||
            dealerStatusEnum === DealerStatusEnum.DeletedForNonPayment
            ? 'deleted'
            : 'active'
    }

    return (
        <div id='CasesEditPage' className='hidden-print'>
            <MessageAlert alert={alert} />

            <StickyHeaderWrapper>
                <div className='row p-2 mt-2'>
                    <div className='col-md-7' style={{ alignSelf: 'center' }}>
                        <span className='pageHeader'>
                            <h3>
                                <a className='btn-sm btn btn-default'
                                    title='Return to List'
                                    onClick={() => history.back()}
                                >
                                    <FontAwesomeIcon icon={['fas', 'reply']} />
                                </a>
                                <span>
                                    {' '}Edit Case - {dealer?.dealerName}
                                </span>
                            </h3>
                        </span>
                    </div>
                    <div className='col-md-5 text-md-end text-start mt-2 mt-lg-0' style={{ alignSelf: 'center' }}>
                        <div>
                            {
                                formIsModified
                                    ? <i id='unsavedMessage' className='font-weight-bold me-1'><Badge bg='danger'><FontAwesomeIcon icon={['fas', 'exclamation-circle']} /> unsaved changes</Badge></i>
                                    : <></>
                            }
                            <DefaultButton
                                className='me-1 btn-sm'
                                variant='default'
                                disabled={loading}
                                onClick={() => navigate(`/cases/add?dealerid=${caseToEdit?.dealerId}&deletedstate=${getDeletedState()}`)}>
                                <FontAwesomeIcon icon={['fas', 'plus-circle']} /> {' '}
                                Add Case
                            </DefaultButton>
                            {
                                caseToEdit?.caseId && caseToEdit?.caseId > 0
                                    ? <Watcher id={caseToEdit?.caseId} watcherType={WatcherTypeEnum.Case} />
                                    : <></>
                            }
                            {!sectionsToHideSaveButton.find(x => x === selectedSection)
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                ? (<SaveButton onClick={(e: any) => submitClick(e)} className='btn btn-sm' />)
                                : null
                            }

                        </div>
                    </div>
                </div>
                <div className='section-divider'></div>
            </StickyHeaderWrapper>

            <SideNavWrapper>
                <SideNav
                    showMenu={showSideNavMenu}>
                    <div className='p-2'>
                        <div className='p-2'>
                            <Badge className={`${badgeColor} shadow-sm w-100 p-2 mb-2 cursor-pointer`}>Status: {dealerStatus?.dealerStatus}</Badge>
                            <div className='bg-white shadow-sm rounded border mb-2 w-100 p-2' style={{ fontSize: '12px' }}>
                                Dealer ID: <a href={`${process.env.REACT_APP_OLD_SYS_ADMIN_URL}/web/Dealers/EditDealer.aspx?id=${caseToEdit?.dealerId}`} target={'_blank'} rel='noopener noreferrer'>{caseToEdit?.dealerId}</a>
                            </div>
                            <div className='bg-white shadow-sm rounded border mb-2 w-100 p-2' style={{ fontSize: '12px' }}>Case Number: {caseToEdit?.caseId}</div>
                            <div className='bg-white shadow-sm rounded border mb-2 w-100 p-2' style={{ fontSize: '12px' }}>
                                <a href={'https://' + dealer?.dealershipWebsite} target='_blank'>View Dealer Website</a>
                            </div>
                            <div hidden={dealer?.guidWebsite == undefined || dealer.guidWebsite == ''} className='bg-white shadow-sm rounded border mb-2 w-100 p-2' style={{ fontSize: '12px' }}>
                                <a href={dealer?.guidWebsite} target='_blank'>{' '}View Guid Website</a>
                            </div>
                        </div>
                    </div>
                    <div className='border-top'>
                        <SideNavMenu>
                            <SideNavMenuItem
                                title='Case Information'
                                active={selectedSection === EditCasesSectionTypeEnum.CASE_SUMMARY}
                                onClick={() => changeSection(EditCasesSectionTypeEnum.CASE_SUMMARY)}
                            />
                            <SideNavMenuItem
                                title='Products'
                                active={selectedSection === EditCasesSectionTypeEnum.PRODUCTS}
                                onClick={() => changeSection(EditCasesSectionTypeEnum.PRODUCTS)}
                            />
                            <SideNavMenuItem
                                title='History'
                                active={selectedSection === EditCasesSectionTypeEnum.HISTORY}
                                onClick={() => changeSection(EditCasesSectionTypeEnum.HISTORY)}
                            />

                            <SideNavMenuItem
                                title='Notes'
                                active={selectedSection === EditCasesSectionTypeEnum.NOTES}
                                onClick={() => changeSection(EditCasesSectionTypeEnum.NOTES)}
                            />

                            <SideNavMenuItem
                                title='Attachments'
                                active={selectedSection === EditCasesSectionTypeEnum.ATTACHMENTS}
                                onClick={() => changeSection(EditCasesSectionTypeEnum.ATTACHMENTS)}
                            />

                            <SideNavMenuItem
                                title='Contact History'
                                active={selectedSection === EditCasesSectionTypeEnum.CONTACT_HISTORY}
                                onClick={() => changeSection(EditCasesSectionTypeEnum.CONTACT_HISTORY)}
                            />

                        </SideNavMenu>
                    </div>

                </SideNav>
                <SideNavContent showMenu={showSideNavMenu}
                    onToggleMenu={() => {
                        setShowSideNavMenu(!showSideNavMenu)
                        localStorage.setItem(LS_SIDEBAR_MENU_STATE, (!showSideNavMenu).toString())
                    }
                    }>
                    <div className={`ms-2 mb-4 ${showSideNavMenu ? 'shown' : 'hidden'}`}>
                        {SectionHandler(selectedSection)}
                    </div>
                </SideNavContent>
            </SideNavWrapper>
        </div>
    )
}
export default CasesEditPage